import { useEffect, useMemo, useRef, useState } from 'react'
import { useRoster, type TabKey } from '../lib/roster'
import { useAuth } from '../lib/session'
import {
  existingTags,
  isSystemTag,
  mergeOutcomes,
  needsConfirming,
  normaliseTag,
  splitByTag,
  tagProblem,
  type TagAction,
  type TagOutcome,
} from '../lib/tagging'
import { addTag, removeTag } from '../lib/tagwrites'

/**
 * Tag or untag everyone on screen at once.
 *
 * Sits beside Export because it acts on the same thing Export does — whatever
 * the current filters have narrowed the roster to — and reuses its menu shell
 * so the two read as one family of "do something with this list" actions.
 *
 * **Untagging matters as much as tagging.** A tag applied to the wrong 386
 * people needs one button to take back, not 386, so every write reports exactly
 * which agents it changed and Undo runs the inverse over precisely those.
 *
 * This is the only feature in the dashboard that writes Renee's own data.
 */
export default function TagMenu({ tab }: { tab: TabKey }) {
  const { visible, tagsByAgent, reloadTags } = useRoster()
  const { appUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [tag, setTag] = useState('')
  const [busy, setBusy] = useState<TagAction | 'undo' | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [done, setDone] = useState<TagOutcome | null>(null)
  const [undone, setUndone] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<TagAction | null>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  /**
   * Moving to another tab clears the result, because the list underneath it is
   * a different list.
   *
   * **Editing the tag box deliberately does NOT clear it.** It used to, and
   * that meant correcting a typo one keystroke after a write took the Undo
   * button away for 386 rows that had just been written — the one moment she is
   * most likely to want it. The result carries its own tag in its wording, so
   * it stays readable next to a box that now says something else.
   */
  useEffect(() => {
    setDone(null)
    setFailed(null)
    setUndone(null)
    setConfirming(null)
  }, [tab])

  const rows = open ? visible(tab) : []
  const known = useMemo(() => existingTags(tagsByAgent), [tagsByAgent])
  const clean = normaliseTag(tag)
  const problem = tag.trim() ? tagProblem(tag) : null
  const system = !!clean && isSystemTag(clean)

  const { withTag, withoutTag } = useMemo(
    () => splitByTag(rows, tagsByAgent, clean),
    [rows, tagsByAgent, clean],
  )

  const ready = !!clean && !problem && busy === null
  const target = (action: TagAction) => (action === 'add' ? withoutTag : withTag)

  const ask = (action: TagAction) => {
    setUndone(null)
    setFailed(null)
    if (needsConfirming(target(action).length, clean)) setConfirming(action)
    else void run(action)
  }

  const run = async (action: TagAction) => {
    const ids = target(action)
    setConfirming(null)
    setBusy(action)
    setFailed(null)
    setUndone(null)

    let result: TagOutcome
    try {
      result =
        action === 'add'
          ? await addTag(ids, clean, appUser?.id ?? null)
          : await removeTag(ids, clean, tagsByAgent)
    } catch (err) {
      console.error('tagging failed', err)
      setFailed('That did not go through. Nothing was changed — try again.')
      setBusy(null)
      return
    }

    /**
     * Everything below is deliberately outside the try.
     *
     * It used to be inside, so a throw from `reloadTags` — or from the render
     * it triggers — landed in the catch above and printed "Nothing was changed"
     * over a write that had committed 386 rows, with no Undo button offered for
     * any of them.
     */
    setBusy(null)
    if (result.error) setFailed(result.error)
    // A partial run still changed rows, so it still gets an Undo. Reporting the
    // error and dropping the undo leaves a half-applied tag and no way back.
    if (result.changed.length > 0) setDone((prev) => mergeOutcomes(prev, result))
    reloadTags()
  }

  /**
   * Undo runs the inverse over exactly the agents the last write touched — not
   * over whatever is on screen now. The filters may well have moved on, and an
   * undo acting on a different list than the thing it is undoing is worse than
   * no undo at all.
   */
  const undo = async () => {
    if (!done) return
    setBusy('undo')
    setFailed(null)

    let result: TagOutcome
    try {
      result =
        done.action === 'add'
          ? await removeTag(done.changed, done.tag, tagsByAgent)
          : await addTag(done.changed, done.tag, appUser?.id ?? null)
    } catch (err) {
      console.error('undo failed', err)
      setFailed('The undo did not go through. Nothing was changed by it — try again.')
      setBusy(null)
      return
    }

    setBusy(null)
    const n = result.changed.length
    if (result.error) {
      setFailed(result.error)
      // Whatever the undo did not reach is still tagged, so keep an undo for it
      // rather than pretending the job is closed.
      setDone({ ...done, changed: done.changed.filter((id) => !result.changed.includes(id)) })
    } else {
      setDone(null)
      setUndone(
        done.action === 'add'
          ? `Undone — "${done.tag}" taken off ${n} ${n === 1 ? 'agent' : 'agents'}.`
          : `Undone — "${done.tag}" put back on ${n} ${n === 1 ? 'agent' : 'agents'}.`,
      )
    }
    reloadTags()
  }

  const doneMessage = (o: TagOutcome) => {
    const n = o.changed.length
    const who = `${n} ${n === 1 ? 'agent' : 'agents'}`
    return o.action === 'add'
      ? `Tagged ${who} as "${o.tag}".`
      : `Removed "${o.tag}" from ${who}.`
  }

  return (
    <div className="exportwrap" ref={box}>
      <button
        className="btn sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Tag
      </button>

      {open && (
        <div className="exportmenu">
          {rows.length === 0 ? (
            <p className="hint">Nothing to tag — no agents match.</p>
          ) : (
            <>
              <p className="cnt">
                {rows.length} {rows.length === 1 ? 'agent' : 'agents'} selected
              </p>

              <label className="f stack">
                <span>Tag</span>
                <input
                  className="areasel-d"
                  list="known-tags"
                  value={tag}
                  placeholder="e.g. ce-2027"
                  onChange={(e) => {
                    setTag(e.target.value)
                    setConfirming(null)
                  }}
                />
              </label>
              <datalist id="known-tags">
                {known.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <p className="hint">
                {known.length === 0
                  ? 'No tags exist yet. Pick a name and it is created by using it.'
                  : `${known.length} tag${known.length === 1 ? '' : 's'} in use. Start typing to pick one.`}
              </p>

              {problem && <p className="error">{problem}</p>}

              {clean && !problem && (
                <p className="hint">
                  <strong>{withTag.length}</strong> already tagged "{clean}" ·{' '}
                  <strong>{withoutTag.length}</strong> not
                </p>
              )}

              {system && (
                <p className="hint">
                  <strong>"{clean}" changes who gets email.</strong> Agents with it are skipped by
                  the outreach system.
                </p>
              )}

              <div aria-live="polite">
                {failed && <p className="error">{failed}</p>}
              </div>

              {confirming ? (
                <>
                  <p className="cnt">
                    {confirming === 'add'
                      ? `Tag ${withoutTag.length} ${withoutTag.length === 1 ? 'agent' : 'agents'} as "${clean}"?`
                      : `Remove "${clean}" from ${withTag.length} ${withTag.length === 1 ? 'agent' : 'agents'}?`}
                  </p>
                  <button className="btn sm" onClick={() => void run(confirming)}>
                    Yes, do it
                  </button>
                  <button className="btn ghost sm" onClick={() => setConfirming(null)}>
                    Cancel
                  </button>
                  <p className="hint">You can undo this straight afterwards.</p>
                </>
              ) : (
                <>
                  <button
                    className="btn sm"
                    disabled={!ready || withoutTag.length === 0}
                    onClick={() => ask('add')}
                  >
                    {busy === 'add'
                      ? 'Working…'
                      : clean && !problem
                        ? `Tag ${withoutTag.length} ${withoutTag.length === 1 ? 'agent' : 'agents'}`
                        : 'Tag them'}
                  </button>
                  <p className="hint">
                    {clean && !problem && withoutTag.length === 0
                      ? 'Everyone in this selection already has that tag.'
                      : 'Adds the tag to everyone on screen who does not already have it.'}
                  </p>

                  <button
                    className="btn sm"
                    disabled={!ready || withTag.length === 0}
                    onClick={() => ask('remove')}
                  >
                    {busy === 'remove'
                      ? 'Working…'
                      : clean && !problem
                        ? `Remove from ${withTag.length} ${withTag.length === 1 ? 'agent' : 'agents'}`
                        : 'Remove it'}
                  </button>
                  <p className="hint">
                    {clean && !problem && withTag.length === 0
                      ? 'Nobody in this selection has that tag.'
                      : 'Takes the tag off everyone on screen who has it. Nothing else about them changes.'}
                  </p>
                </>
              )}

              <div aria-live="polite">
                {done && done.changed.length > 0 && (
                  <>
                    <p className="cnt">{doneMessage(done)}</p>
                    <button className="btn sm" disabled={busy !== null} onClick={() => void undo()}>
                      {busy === 'undo' ? 'Working…' : 'Undo that'}
                    </button>
                    <p className="hint">
                      Undo acts on exactly those {done.changed.length}, even if the filters have
                      moved on since.
                    </p>
                  </>
                )}
                {undone && <p className="cnt">{undone}</p>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
